"use client";

import React from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { Card, CardContent } from "@/components/ui/card";
import {
  BadgeIndianRupee,
  BarChart3,
  Globe2,
  Package,
  Percent,
  ShieldAlert,
  Truck,
  Users,
} from "lucide-react";

function formatCompact(value: number) {
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatPercent(value: number) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function InsightCard({
  icon,
  label,
  value,
  description,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  description: string;
  accent: {
    bar: string;
    chip: string;
    glow: string;
    label: string;
  };
}) {
  return (
    <Card className="group relative overflow-hidden border-border/60 bg-card/85 shadow-theme-sm backdrop-blur-xl transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg">
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent.bar}`}
      />
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br ${accent.glow} blur-2xl transition-opacity duration-300 group-hover:opacity-100`}
      />
      <CardContent className="relative p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p
              className={`text-[11px] font-medium uppercase tracking-[0.18em] ${accent.label}`}
            >
              {label}
            </p>
            <p className="mt-1.5 text-xl font-semibold tracking-tight text-foreground">
              {value}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {description}
            </p>
          </div>
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 ${accent.chip} shadow-sm`}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardInsights() {
  const dashboard = useSelector((state: RootState) => state.dashboard);
  const stats = dashboard.stats ?? {};
  const snapshot = dashboard.snapshot ?? {};
  const customers = Array.isArray(dashboard.customers)
    ? dashboard.customers
    : [];
  const topProducts = Array.isArray(dashboard.topProducts)
    ? dashboard.topProducts
    : [];
  const campaigns = Array.isArray(dashboard.campaignData?.monthly_campaigns)
    ? dashboard.campaignData.monthly_campaigns
    : Array.isArray(snapshot?.campaign_data?.monthly_campaigns)
      ? snapshot.campaign_data.monthly_campaigns
      : [];
  const stateData = Array.isArray(dashboard.salesByState?.state_data)
    ? dashboard.salesByState.state_data
    : Array.isArray(snapshot?.sales_by_state?.state_data)
      ? snapshot.sales_by_state.state_data
      : [];
  const deliveryData =
    dashboard.salesByCountries ?? snapshot.sales_by_countries ?? {};
  const traffic = dashboard.trafficSources ?? snapshot.traffic_sources ?? {};
  const stock = dashboard.stockAnalysis ?? snapshot.stock_analysis ?? {};

  const totalSales = Number(stats.total_sales || 0);
  const totalOrders = Number(stats.total_orders || 0);
  const avgOrderValue =
    totalOrders > 0
      ? totalSales / totalOrders
      : Number(customers[0]?.average_order_value || 0);
  const topState =
    snapshot?.sales_by_state?.top_state || stateData[0]?.state || "N/A";
  const topStateSales = Number(
    snapshot?.sales_by_state?.top_state_sales || stateData[0]?.sales || 0,
  );
  const stateSalesTotal = stateData.reduce(
    (sum: number, row: any) => sum + Number(row?.sales || 0),
    0,
  );
  const stateShare =
    stateSalesTotal > 0 ? (topStateSales / stateSalesTotal) * 100 : 0;
  const lowStockCount = Array.isArray(stock.stock_data)
    ? stock.stock_data.filter((item: any) => Number(item?.quantity || 0) <= 10)
        .length
    : 0;
  const emptyStockPercentage = Number(stock.empty_stock_percentage || 0);
  const activeDeliveries = Number(deliveryData.active_deliveries || 0);
  const recentDeliveries = Number(deliveryData.recent_deliveries || 0);
  const conversionRate = Number(traffic.checkout_conversion || 0);
  const bounceRate = Number(traffic.bounce_rate || 0);
  const returnRate = Number(stats.returning_customer_rate || 0);
  const trafficVisitors = Number(traffic.total_visitors || 0);

  const insightCards = [
    {
      icon: <BadgeIndianRupee className="h-5 w-5" />,
      label: "Average order value",
      value: avgOrderValue ? formatMoney(avgOrderValue) : "N/A",
      description: `${formatCompact(totalOrders)} orders · ${formatCompact(Number(customers.length))} customers`,
      accent: {
        bar: "from-emerald-400 via-teal-400 to-cyan-400",
        chip: "bg-gradient-to-br from-emerald-100 via-teal-100 to-cyan-100 text-emerald-700 dark:from-emerald-500/15 dark:via-teal-500/15 dark:to-cyan-500/15 dark:text-cyan-200",
        glow: "from-emerald-400/25 via-teal-400/15 to-cyan-400/10",
        label: "text-emerald-700 dark:text-emerald-300",
      },
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      label: "Regional leader",
      value: topState,
      description: `${formatMoney(topStateSales)} from ${formatPercent(stateShare)} of state sales`,
      accent: {
        bar: "from-fuchsia-400 via-violet-400 to-indigo-400",
        chip: "bg-gradient-to-br from-fuchsia-100 via-violet-100 to-indigo-100 text-violet-700 dark:from-fuchsia-500/15 dark:via-violet-500/15 dark:to-indigo-500/15 dark:text-violet-200",
        glow: "from-fuchsia-400/20 via-violet-400/15 to-indigo-400/10",
        label: "text-violet-700 dark:text-violet-300",
      },
    },
    {
      icon: <ShieldAlert className="h-5 w-5" />,
      label: "Stock pressure",
      value: `${emptyStockPercentage || 0}%`,
      description: `${lowStockCount} low-stock items · inventory watch`,
      accent: {
        bar: "from-amber-400 via-orange-400 to-rose-400",
        chip: "bg-gradient-to-br from-amber-100 via-orange-100 to-rose-100 text-amber-700 dark:from-amber-500/15 dark:via-orange-500/15 dark:to-rose-500/15 dark:text-amber-200",
        glow: "from-amber-400/25 via-orange-400/15 to-rose-400/10",
        label: "text-amber-700 dark:text-amber-300",
      },
    },
    {
      icon: <Truck className="h-5 w-5" />,
      label: "Delivery pulse",
      value: formatCompact(activeDeliveries),
      description: `${formatCompact(recentDeliveries)} recent deliveries · live dispatch`,
      accent: {
        bar: "from-sky-400 via-cyan-400 to-slate-400",
        chip: "bg-gradient-to-br from-sky-100 via-cyan-100 to-slate-100 text-sky-700 dark:from-sky-500/15 dark:via-cyan-500/15 dark:to-slate-500/15 dark:text-cyan-200",
        glow: "from-sky-400/25 via-cyan-400/15 to-slate-400/10",
        label: "text-sky-700 dark:text-sky-300",
      },
    },
    {
      icon: <Percent className="h-5 w-5" />,
      label: "Conversion health",
      value: formatPercent(conversionRate),
      description: `${formatPercent(bounceRate)} bounce rate · traffic ${formatCompact(trafficVisitors)}`,
      accent: {
        bar: "from-lime-400 via-emerald-400 to-green-400",
        chip: "bg-gradient-to-br from-lime-100 via-emerald-100 to-green-100 text-green-700 dark:from-lime-500/15 dark:via-emerald-500/15 dark:to-green-500/15 dark:text-emerald-200",
        glow: "from-lime-400/25 via-emerald-400/15 to-green-400/10",
        label: "text-green-700 dark:text-green-300",
      },
    },
    {
      icon: <Package className="h-5 w-5" />,
      label: "Top catalogue",
      value: formatCompact(topProducts.length),
      description: `${formatCompact(Number(campaigns.length))} campaigns · ${formatPercent(returnRate)} returning customers`,
      accent: {
        bar: "from-orange-400 via-amber-400 to-yellow-400",
        chip: "bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100 text-orange-700 dark:from-orange-500/15 dark:via-amber-500/15 dark:to-yellow-500/15 dark:text-orange-200",
        glow: "from-orange-400/25 via-amber-400/15 to-yellow-400/10",
        label: "text-orange-700 dark:text-orange-300",
      },
    },
    {
      icon: <Users className="h-5 w-5" />,
      label: "Customer base",
      value: formatCompact(
        Number(stats.customer_count || customers.length || 0),
      ),
      description: `${formatPercent(returnRate)} returning rate · lifetime value trend`,
      accent: {
        bar: "from-pink-400 via-rose-400 to-red-400",
        chip: "bg-gradient-to-br from-pink-100 via-rose-100 to-red-100 text-rose-700 dark:from-pink-500/15 dark:via-rose-500/15 dark:to-red-500/15 dark:text-rose-200",
        glow: "from-pink-400/25 via-rose-400/15 to-red-400/10",
        label: "text-rose-700 dark:text-rose-300",
      },
    },
    {
      icon: <Globe2 className="h-5 w-5" />,
      label: "Market breadth",
      value: formatCompact(stateData.length),
      description: `${formatCompact(trafficVisitors)} visitors · ${formatCompact(activeDeliveries)} active deliveries`,
      accent: {
        bar: "from-cyan-400 via-sky-400 to-blue-400",
        chip: "bg-gradient-to-br from-cyan-100 via-sky-100 to-blue-100 text-sky-700 dark:from-cyan-500/15 dark:via-sky-500/15 dark:to-blue-500/15 dark:text-sky-200",
        glow: "from-cyan-400/25 via-sky-400/15 to-blue-400/10",
        label: "text-sky-700 dark:text-sky-300",
      },
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      {insightCards.map((card) => (
        <InsightCard key={card.label} {...card} />
      ))}
    </div>
  );
}
